import { useCallback, useEffect, type ReactNode } from 'react'
import { cheapestPlanWith, isSkillTierAllowed, type SkillTier } from '@highlight/common'
import { usePricingEdition } from '@renderer/chat/hooks/use-plan-features'
import { usePlanAllowances } from '@renderer/chat/hooks/use-plan-allowances'
import { usePaywallState } from '@renderer/chat/store/paywalls'
import { resetDateLabel } from '@renderer/components/pricing/limits-copy'
import { ActionTagArea } from '@renderer/components/action-tags'
import { applyMetadataTransitions } from '@renderer/components/action-tags/status-transition'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { subscriptionPlansRoute } from '@shared/settings/settings-deeplink'
import { openSubscriptionSettings } from '../../../components/sidepanel/plan-banners/sidepanel-plan-banners'
import type { StagedAction } from '../../../hooks/staged-actions/types'
import { useUnlockAction } from '../use-unlock-action'
import { useActionArchiveControl } from './action-utilities'
import { LockedActionPreview } from './locked-action-preview'

const SURFACE = 'action view'

interface LockedActionBodyProps {
  action: StagedAction
  tier: SkillTier
  /** The action's header, drawn inside the locked page so it sits in the blur's clear band. */
  header: ReactNode
  /** Offers Dismiss, which archives the action and closes the view: only where the action is the view. */
  dismissible?: boolean
}

/** The locked preview's page, wired to the unblur allowance and the plans page. */
export function LockedActionBody({ action, tier, header, dismissible = false }: LockedActionBodyProps) {
  const plan = cheapestPlanWith(usePricingEdition(), (features) => isSkillTierAllowed(features, tier))
  const allowances = usePlanAllowances()
  const unlock = useUnlockAction(action.id)
  const archive = useActionArchiveControl(action)
  const { isLoading: isPlanLoading } = usePaywallState()
  // Offered only once billing loads: out of credits, the credits lock takes over, and an unblur then would be wasted.
  // A plan without the cap never fetches a count, and a failed read has none, so both get the upgrade without one.
  const unlocks = isPlanLoading || allowances.isLoading ? undefined : (allowances.data?.actionUnlocks ?? null)

  useEffect(() => {
    AmplitudeRenderer.trackEvent('Paywall', { action: 'shown', feature: 'locked action', plan, from: SURFACE })
  }, [plan])

  const onUpgrade = useCallback(() => {
    AmplitudeRenderer.trackEvent('Paywall', { action: 'clicked', feature: 'locked action', plan, from: SURFACE })
    openSubscriptionSettings(subscriptionPlansRoute())
  }, [plan])

  const { mutate: unblur } = unlock
  const onUnblur = useCallback(() => {
    AmplitudeRenderer.trackEvent('Paywall', { action: 'unblurred', feature: 'locked action', plan, from: SURFACE })
    unblur(undefined, { onError: (error) => console.error(error) })
  }, [plan, unblur])

  const tags = action.view?.tags ?? []
  return (
    <LockedActionPreview
      header={header}
      toolSet={action.toolSet}
      fields={
        tags.length > 0 && (
          <ActionTagArea
            className="mb-3"
            tags={applyMetadataTransitions(tags, action.view?.nestedViews)}
            tagOrder={action.view?.tagOrder}
            toolSet={action.toolSet}
          />
        )
      }
      plan={plan}
      unlocks={unlocks}
      resets={resetDateLabel(allowances.data?.periodEndsAt)}
      isUnblurring={unlock.isPending}
      onUnblur={onUnblur}
      onUpgrade={onUpgrade}
      onDismiss={dismissible ? archive.onSelect : undefined}
    />
  )
}
